/**
 * Dynamic "returnQuestion" info/warning messages shown beneath certain answers.
 *
 * These are computed in a single place so the inline quiz flow (useQuizAnswer)
 * and the edit flow (EditFieldModal) always stay in sync — otherwise a message
 * shows when first answering but disappears (or fails to appear) after editing.
 *
 * Mirrors the legacy app (QuizRepresentative.jsx / RepModalComponent.jsx).
 */
import { SECTION_KEYS, SectionKey, ANSWERS, QUESTION_KEYS } from '../constants/quizConstants';

export const RETURN_MESSAGES = {
  // Prevented caregiver → substitute caregiver transition (after absenceReason)
  PREVENTED_TRANSITION:
    'Vielen Dank für die Informationen. Nun geht es um die Person, die statt der verhinderten Person die Pflege übernommen hat oder übernehmen wird.',
  // Substitute caregiver
  PRIVATE_CAREGIVER: 'Vertretende Person ist privat und nur gelegentlich als PflegerIn tätig.',
  RELATIONSHIP_WARNING:
    'Ersatz von Kosten der Verhinderungspflege ist bei Personen in Verwandschaft, Lebensgemeinschaft und Ehepartnern nur eingeschränkt möglich.',
  MARRIAGE_RELATIONSHIP_WARNING:
    'Ersatz von Kosten der Verhinderungspflege ist bei Verschwägerten Personen nur eingeschränkt möglich.',
  SAME_HOUSEHOLD_WARNING:
    'Ersatz von Kosten der Verhinderungspflege ist bei dauernd im gleichen Haushalt lebenden Personen nur eingeschränkt möglich.',
  HOW_FOUND_TRANSITION: 'Bitte teilen Sie mir die Details zu der verrichteten Verhinderungspflege mit.',
  // Private individual
  BANK_DATA_INFO:
    'Sie können nun noch Bankdaten eingeben. Wenn es Ihnen lieber ist, können Sie dies auch handschriftlich nachtragen.',
  IBAN_WARNING:
    'Beachten Sie, dass ich die IBAN nicht prüfe. Bitte prüfen Sie selbst, ob die eingegebene IBAN korrekt eingegeben wurde.',
} as const;

// Relationship options that trigger the warning (1st and 2nd degree relatives)
const RELATED_RELATIONSHIP_OPTIONS = [
  'Verwandt 1 Grad : Vater, Mutter, Sohn oder Tochter des Berechtigten',
  'Verwandt 2. Grades: Schwester, Bruder, Grossmutter, Grossvater, Enkeltochter oder Enkelsohn des Berechtigten',
];

// Marriage relationship options that trigger the warning
const MARRIAGE_RELATIONSHIP_WARNING_OPTIONS = [
  'Ehepartner oder eingetragener Lebenspartner',
  'Verschwägert 1a) Ehepartner / Lebenspartner von Vater Mutter Sohn Tochter des Berechtigten',
  'Verschwägert 1b) Vater Mutter Sohn Tochter des Ehepartner / Lebenspartners des Berechtigten',
  'Verschwägert 2a) Ehepartner von Schwester Bruder Grossmutter Grossvater Enkeltochter Enkelsohn des Berechtigten',
  'Verschwägert 2b) Schwester Bruder Grossmutter Grossvater Enkeltochter Enkelsohn des Ehepartnes / Lebenspartners des Berechtigten',
];

/**
 * Returns the dynamic info/warning message for a given answer, or '' if none
 * applies. Callers should always dispatch the result (the empty string clears a
 * previously shown message when the answer is changed to a non-triggering one).
 *
 * Covers the Substitute Caregiver and Private Individual sections; other
 * sections (birthday age warning, care-level flow stop) are handled separately
 * because they carry additional state (links, age calculation).
 */
export const getReturnMessage = (
  section: SectionKey,
  questionKey: string,
  answer: string
): string => {
  if (section === SECTION_KEYS.PREVENTED_CAREGIVER) {
    // Only absenceReason carries a message here. The flow-stop messages for
    // isProfessionalCarer / carePeriod are handled separately (FLOW_STOP_CONFIG
    // and EditFieldModal.resetDependentQuestions) and must not be touched.
    if (questionKey === QUESTION_KEYS.ABSENCE_REASON) {
      return answer ? RETURN_MESSAGES.PREVENTED_TRANSITION : '';
    }
    return '';
  }

  if (section === SECTION_KEYS.SUBSTITUTE_CAREGIVER) {
    switch (questionKey) {
      case QUESTION_KEYS.IS_COMMERCIALLY_ACTIVE:
        return answer === ANSWERS.NO ? RETURN_MESSAGES.PRIVATE_CAREGIVER : '';
      case QUESTION_KEYS.RELATIONSHIP_TYPE:
        return RELATED_RELATIONSHIP_OPTIONS.includes(answer)
          ? RETURN_MESSAGES.RELATIONSHIP_WARNING
          : '';
      case QUESTION_KEYS.MARRIAGE_RELATIONSHIP:
        return MARRIAGE_RELATIONSHIP_WARNING_OPTIONS.includes(answer)
          ? RETURN_MESSAGES.MARRIAGE_RELATIONSHIP_WARNING
          : '';
      case QUESTION_KEYS.SAME_HOUSEHOLD:
        return answer === ANSWERS.YES ? RETURN_MESSAGES.SAME_HOUSEHOLD_WARNING : '';
      case QUESTION_KEYS.HOW_FOUND:
        return answer ? RETURN_MESSAGES.HOW_FOUND_TRANSITION : '';
      default:
        return '';
    }
  }

  if (section === SECTION_KEYS.PRIVATE_INDIVIDUAL) {
    switch (questionKey) {
      // Bank-data hint is shown ONLY when there is no earnings loss (answer
      // "Nein"). When "Ja", the amount is asked and no hint is shown afterwards.
      case QUESTION_KEYS.HAS_EARNINGS_LOSS:
        return answer === ANSWERS.NO ? RETURN_MESSAGES.BANK_DATA_INFO : '';
      case QUESTION_KEYS.IBAN:
        return answer ? RETURN_MESSAGES.IBAN_WARNING : '';
      default:
        return '';
    }
  }

  return '';
};
